import type { GridFuelMixEntry, GridSummary, LayerState } from '@vitals/shared';
import type { Vital, VitalTone, VitalView } from './vitals.js';
import { vitalIcon } from './icons.js';

const PRICE_WARN_THRESHOLD = 150;
const PRICE_BAD_THRESHOLD = 500;

const CARBON_FREE_FUELS: ReadonlySet<string> = new Set([
  'Nuclear',
  'Hydro',
  'Wind',
  'Other Renewables',
]);

const SOURCE_COLORS: Readonly<Record<string, string>> = {
  'Dual Fuel': '#ef8354',
  'Natural Gas': '#ffb74d',
  Nuclear: '#b388ff',
  Hydro: '#4fc3f7',
  Wind: '#80cbc4',
  'Other Fossil Fuels': '#a1887f',
  'Other Renewables': '#66bb6a',
};
const UNKNOWN_SOURCE_COLOR = '#78909c';

const gridTime = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});

function toneForPrice(lbmp: number | null | undefined): VitalTone | undefined {
  if (typeof lbmp !== 'number') return undefined;
  if (lbmp >= PRICE_BAD_THRESHOLD) return 'bad';
  if (lbmp >= PRICE_WARN_THRESHOLD) return 'warn';
  return undefined;
}

function summaryOf(state: LayerState | undefined): GridSummary | null {
  if (!state || state.status === 'down' || !state.summary) return null;
  return state.summary as unknown as GridSummary;
}

/** Parse a NYISO timestamp as display-only New York wall time. */
function gridDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value === '') return null;
  const match = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, month, day, year, hour, minute, second] = match;
  const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  if (!Number.isFinite(date.getTime())) return null;
  if (
    date.getUTCFullYear() !== Number(year)
    || date.getUTCMonth() + 1 !== Number(month)
    || date.getUTCDate() !== Number(day)
    || date.getUTCHours() !== Number(hour)
    || date.getUTCMinutes() !== Number(minute)
    || date.getUTCSeconds() !== Number(second)
  ) return null;
  return date;
}

function newestDisplayedTimestamp(summary: GridSummary): Date | null {
  const candidates: unknown[] = [summary.at];
  if (typeof summary.lbmp === 'number') candidates.push(summary.lbmpAt);
  if (Array.isArray(summary.mix) && summary.mix.length > 0) candidates.push(summary.mixAt);
  return candidates
    .map(gridDate)
    .filter((date): date is Date => date !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;
}

function positiveSources(mix: GridFuelMixEntry[] | null | undefined): GridFuelMixEntry[] {
  if (!Array.isArray(mix)) return [];
  return mix
    .filter((source) => Number.isFinite(source.mw) && source.mw > 0)
    .sort((a, b) => b.mw - a.mw || a.fuel.localeCompare(b.fuel));
}

function gridDetails(summary: GridSummary): NonNullable<VitalView['details']> {
  const load = `${Math.round(summary.mw).toLocaleString('en-US')} MW`;
  const details: NonNullable<VitalView['details']> = [
    { kind: 'metric', label: 'NYC load', value: load },
  ];

  if (typeof summary.lbmp === 'number') {
    details.push({
      kind: 'metric',
      label: 'Wholesale price',
      value: `$${Math.round(summary.lbmp).toLocaleString('en-US')}/MWh`,
    });
  }

  const sources = positiveSources(summary.mix);
  const total = sources.reduce((sum, source) => sum + source.mw, 0);
  if (total > 0) {
    const carbonFree = sources
      .filter((source) => CARBON_FREE_FUELS.has(source.fuel))
      .reduce((sum, source) => sum + source.mw, 0);
    details.push({
      kind: 'metric',
      label: 'Carbon-free',
      value: `${Math.round(carbonFree / total * 100)}%`,
    });

    const maxMw = sources[0]!.mw;
    for (const source of sources) {
      details.push({
        kind: 'category',
        label: source.fuel,
        value: `${Math.round(source.mw).toLocaleString('en-US')} MW`,
        percent: `${Math.round(source.mw / total * 100)}%`,
        frac: source.mw / maxMw,
        color: SOURCE_COLORS[source.fuel] ?? UNKNOWN_SOURCE_COLOR,
      });
    }
  }

  const through = newestDisplayedTimestamp(summary);
  if (through) details.push({ kind: 'note', text: `Through ${gridTime.format(through)} ET` });
  return details;
}

/** NYC load headline with wholesale-price and individual statewide generation details. */
export const gridVital: Vital = {
  id: 'grid',
  layerId: 'grid_load',
  group: 'city',
  icon: vitalIcon('grid'),
  select(state: LayerState | undefined): VitalView {
    const summary = summaryOf(state);
    const stale = state?.status === 'stale';
    if (!summary) {
      return { value: '—', label: 'power grid', down: true, stale };
    }
    return {
      value: `${Math.round(summary.mw).toLocaleString('en-US')} MW`,
      label: 'power grid',
      down: false,
      stale,
      tone: toneForPrice(summary.lbmp),
      detailTitle: 'New York power grid',
      details: gridDetails(summary),
    };
  },
};
